import { AdminPage, AdminPageHeader } from '../../components/layout/AdminPage';
/**
 * @page VenueSettingsPage
 * @description 場館設置頁面（系統管理下）
 * 功能：場館 Logo 上傳 + 樓層與牌桌管理
 * Route: /admin/venue
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api.ts';

// ─── Types ──────────────────────────────────────────────────────────────────

interface VenueTable {
  id: string;
  tableNo: number;
  name: string | null;
  floor: string;
  maxSeats: number;
  isActive: boolean;
}

interface Tournament {
  id: string;
  name: string;
  status: string;
}

interface TableAssignment {
  id: string;
  tournamentId: string;
  tableId: string;
  isFinalTable: boolean;
}

// ─── Table Form (Create/Edit) ───────────────────────────────────────────────

interface TableFormData {
  tableNo: number;
  name: string;
  floor: string;
  maxSeats: number;
}

function TableForm({
  initial,
  floors,
  onSave,
  onCancel,
  loading,
}: {
  initial?: Partial<TableFormData>;
  floors: string[];
  onSave: (data: TableFormData) => void;
  onCancel: () => void;
  loading: boolean;
}) {
  const { t } = useTranslation('venue');
  const [tableNo, setTableNo] = useState(initial?.tableNo ?? 1);
  const [name, setName] = useState(initial?.name ?? '');
  const [floor, setFloor] = useState(initial?.floor ?? '1F');
  const [maxSeats, setMaxSeats] = useState(initial?.maxSeats ?? 9);
  const [newFloor, setNewFloor] = useState('');

  const inputCls = 'input input-sm input-bordered w-full';

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold mb-1">{t('tableNo')}</label>
          <input
            type="number"
            min={1}
            className={inputCls}
            value={tableNo}
            onChange={(e) => setTableNo(Number(e.target.value))}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1">{t('tableName')}</label>
          <input
            className={inputCls}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('tableNamePlaceholder')}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold mb-1">{t('floor')}</label>
          <div className="flex gap-2">
            <select className={inputCls} value={floor} onChange={(e) => setFloor(e.target.value)}>
              {floors.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
              {newFloor && !floors.includes(newFloor) && (
                <option value={newFloor}>{newFloor}</option>
              )}
            </select>
            <input
              className={`${inputCls} w-20`}
              placeholder={t('newFloor')}
              value={newFloor}
              onChange={(e) => {
                setNewFloor(e.target.value);
                if (e.target.value) setFloor(e.target.value);
              }}
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1">{t('maxSeats')}</label>
          <input
            type="number"
            min={2}
            max={12}
            className={inputCls}
            value={maxSeats}
            onChange={(e) => setMaxSeats(Number(e.target.value))}
          />
        </div>
      </div>
      <div className="flex gap-2 justify-end pt-2">
        <button onClick={onCancel} className="btn btn-ghost btn-sm">
          {t('cancel')}
        </button>
        <button
          disabled={loading || tableNo < 1}
          onClick={() => onSave({ tableNo, name, floor, maxSeats })}
          className="btn btn-primary btn-sm"
        >
          {loading ? t('saving') : t('save')}
        </button>
      </div>
    </div>
  );
}

// ─── Table Card ─────────────────────────────────────────────────────────────

function TableCard({
  table,
  assignment,
  tournaments,
  onEdit,
  onDelete,
  onAssign,
  onUnassign,
}: {
  table: VenueTable;
  assignment: TableAssignment | null;
  tournaments: Tournament[];
  onEdit: () => void;
  onDelete: () => void;
  onAssign: (tournamentId: string) => void;
  onUnassign: () => void;
}) {
  const { t } = useTranslation('venue');
  const assignedTournament = assignment
    ? tournaments.find((tr) => tr.id === assignment.tournamentId)
    : null;
  const activeTournaments = tournaments.filter((tr) =>
    ['DRAFT', 'REGISTERED', 'IN_PROGRESS', 'PAUSED'].includes(tr.status),
  );

  return (
    <div className="bg-base-200 border border-base-300 rounded-xl p-4 flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary font-black text-lg">
            {table.tableNo}
          </div>
          <div>
            <p className="font-bold text-sm text-base-content">
              {table.name || `${t('table')} ${table.tableNo}`}
            </p>
            <p className="text-xs text-base-content/50">
              {table.maxSeats} {t('seats')}
            </p>
          </div>
        </div>
        <div className="flex gap-1">
          <button onClick={onEdit} className="btn btn-ghost btn-xs btn-square">
            <span className="material-symbols-outlined text-base">edit</span>
          </button>
          <button onClick={onDelete} className="btn btn-ghost btn-xs btn-square text-error">
            <span className="material-symbols-outlined text-base">delete</span>
          </button>
        </div>
      </div>

      {/* Assignment status */}
      {assignedTournament ? (
        <div className="flex items-center justify-between bg-success/10 border border-success/20 rounded-lg px-3 py-2">
          <div className="flex items-center gap-2">
            <span className="flex h-2 w-2 rounded-full bg-success animate-pulse" />
            <span className="text-xs font-bold text-success">{assignedTournament.name}</span>
          </div>
          <button
            onClick={onUnassign}
            className="text-xs text-base-content/40 hover:text-error transition-colors"
          >
            {t('unassign')}
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <select
            className="select select-xs select-bordered flex-1"
            defaultValue=""
            onChange={(e) => {
              if (e.target.value) onAssign(e.target.value);
            }}
          >
            <option value="">{t('assignToTournament')}</option>
            {activeTournaments.map((tr) => (
              <option key={tr.id} value={tr.id}>
                {tr.name}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export default function VenueSettingsPage() {
  const { t } = useTranslation('venue');
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);

  // Queries
  const { data: tables = [], isLoading } = useQuery<VenueTable[]>({
    queryKey: ['venue-tables'],
    queryFn: () => api.get<VenueTable[]>('/venue/tables'),
  });

  const { data: tournaments = [] } = useQuery<Tournament[]>({
    queryKey: ['tournaments-active'],
    queryFn: () =>
      api
        .get<{ data: Tournament[]; total: number }>('/tournament?limit=50')
        .then((d) => d.data ?? []),
    staleTime: 0,
    refetchOnMount: 'always',
  });

  // Get all table assignments for active tournaments
  const activeTournamentIds = tournaments
    .filter((tr) => ['DRAFT', 'REGISTERED', 'IN_PROGRESS', 'PAUSED'].includes(tr.status))
    .map((tr) => tr.id);

  const { data: allAssignments = [] } = useQuery<TableAssignment[]>({
    queryKey: ['table-assignments', activeTournamentIds],
    queryFn: async () => {
      const results: TableAssignment[] = [];
      for (const tid of activeTournamentIds) {
        try {
          // API returns array of { id, tournamentId, tableId, isFinalTable, table, seats }
          const layout = await api.get<
            Array<{ id: string; tournamentId: string; tableId: string; isFinalTable: boolean }>
          >(`/venue/tournaments/${tid}/layout`);
          for (const ta of layout) {
            results.push({
              id: ta.id,
              tournamentId: ta.tournamentId,
              tableId: ta.tableId,
              isFinalTable: ta.isFinalTable,
            });
          }
        } catch (err) {
          console.warn(`[VenueSettings] Failed to fetch table layout for tournament ${tid}:`, err);
        }
      }
      return results;
    },
    enabled: activeTournamentIds.length > 0,
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: { tableNo: number; name?: string; floor?: string; maxSeats?: number }) =>
      api.post('/venue/tables', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['venue-tables'] });
      setShowAdd(false);
    },
    onError: (err: Error) => {
      console.warn('createTable failed:', err.message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string;
      name?: string;
      floor?: string;
      maxSeats?: number;
    }) => api.patch(`/venue/tables/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['venue-tables'] });
      setEditId(null);
    },
    onError: (err: Error) => {
      console.warn('updateTable failed:', err.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/venue/tables/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['venue-tables'] }),
    onError: (err: Error) => {
      console.warn('deleteTable failed:', err.message);
    },
  });

  const assignMutation = useMutation({
    mutationFn: ({ tournamentId, tableId }: { tournamentId: string; tableId: string }) =>
      api.post(`/venue/tournaments/${tournamentId}/tables`, { tableId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['table-assignments'] }),
    onError: (err: Error) => {
      console.warn('assignTable failed:', err.message);
    },
  });

  const unassignMutation = useMutation({
    mutationFn: ({ tournamentId, tableId }: { tournamentId: string; tableId: string }) =>
      api.delete(`/venue/tournaments/${tournamentId}/tables/${tableId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['table-assignments'] }),
    onError: (err: Error) => {
      console.warn('unassignTable failed:', err.message);
    },
  });

  // Group tables by floor
  const floors = [...new Set(tables.map((t) => t.floor))].sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true }),
  );
  if (floors.length === 0) floors.push('1F');

  const getAssignment = (tableId: string) =>
    allAssignments.find((a) => a.tableId === tableId) ?? null;

  return (
    <AdminPage className="admin-page-body">
      {/* Page Header */}
      <AdminPageHeader title={t('title')} description={t('subtitle')} />

      {/* Tables Section */}
      <section className="bg-base-200/50 border border-base-300 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-base-content">{t('floorAndTables')}</h2>
          <button onClick={() => setShowAdd(true)} className="btn btn-primary btn-sm gap-2">
            <span className="material-symbols-outlined text-base">add</span>
            {t('addTable')}
          </button>
        </div>

        {/* Add Form */}
        {showAdd && (
          <div className="mb-6 p-4 bg-base-300/50 border border-base-300 rounded-xl">
            <h3 className="font-bold text-sm mb-3">{t('addTable')}</h3>
            <TableForm
              floors={floors}
              initial={{
                tableNo: tables.length > 0 ? Math.max(...tables.map((t) => t.tableNo)) + 1 : 1,
                floor: floors[0],
              }}
              onSave={(data) => createMutation.mutate(data)}
              onCancel={() => setShowAdd(false)}
              loading={createMutation.isPending}
            />
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-12 text-base-content/40">{t('loading')}</div>
        ) : tables.length === 0 ? (
          <div className="text-center py-12 text-base-content/40">
            <span className="material-symbols-outlined text-5xl block mb-3">table_restaurant</span>
            {t('noTables')}
          </div>
        ) : (
          <div className="space-y-6">
            {floors.map((floor) => {
              const floorTables = tables.filter((t) => t.floor === floor);
              if (floorTables.length === 0) return null;
              return (
                <div key={floor}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-primary font-bold text-sm">{floor}</span>
                    <div className="flex-1 h-px bg-primary/20" />
                    <span className="text-xs text-base-content/40">
                      {floorTables.length} {t('tablesCount')}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {floorTables.map((table) =>
                      editId === table.id ? (
                        <div
                          key={table.id}
                          className="bg-base-300/50 border border-base-300 rounded-xl p-4"
                        >
                          <TableForm
                            initial={{
                              tableNo: table.tableNo,
                              name: table.name ?? '',
                              floor: table.floor,
                              maxSeats: table.maxSeats,
                            }}
                            floors={floors}
                            onSave={(data) =>
                              updateMutation.mutate({
                                id: table.id,
                                name: data.name,
                                floor: data.floor,
                                maxSeats: data.maxSeats,
                              })
                            }
                            onCancel={() => setEditId(null)}
                            loading={updateMutation.isPending}
                          />
                        </div>
                      ) : (
                        <TableCard
                          key={table.id}
                          table={table}
                          assignment={getAssignment(table.id)}
                          tournaments={tournaments}
                          onEdit={() => setEditId(table.id)}
                          onDelete={() => {
                            if (
                              confirm(
                                t('confirmDelete', {
                                  name: table.name || `${t('table')} ${table.tableNo}`,
                                }),
                              )
                            ) {
                              deleteMutation.mutate(table.id);
                            }
                          }}
                          onAssign={(tournamentId) =>
                            assignMutation.mutate({ tournamentId, tableId: table.id })
                          }
                          onUnassign={() => {
                            const a = getAssignment(table.id);
                            if (a)
                              unassignMutation.mutate({
                                tournamentId: a.tournamentId,
                                tableId: table.id,
                              });
                          }}
                        />
                      ),
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </AdminPage>
  );
}
